import { fetchServer } from "@/features/utils/fetch-server";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface BookingEvent {
  id: string;
  summary: string;
  description: string;
  location: string;
  status: string;
  start_time: number;
  end_time: number;
  is_all_day: boolean;
  timezone: string;
  is_recurring: boolean;
  recurrence_rule?: string;
  /** Video meeting link, when the appointment is held online. */
  conference_url?: string;
  attendees: Array<{
    email: string;
    name: string;
  }>;
}

// Fetch booking events for a public session
export function usePublicBooking(publicId: string) {
  return useQuery({
    queryKey: ["public-booking", publicId],
    queryFn: async (): Promise<BookingEvent[]> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/booking`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch booking events");
      }

      return response.json();
    },
    enabled: !!publicId,
  });
}

// Confirm a booking event
export function useConfirmBookingEvent(publicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string): Promise<{ success: boolean }> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/booking/${eventId}`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to confirm event");
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate booking query to refetch updated events
      queryClient.invalidateQueries({
        queryKey: ["public-booking", publicId],
      });
    },
  });
}

// Cancel a booking event
export function useCancelBookingEvent(publicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string): Promise<{ success: boolean }> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/booking/${eventId}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to cancel event");
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate booking query to refetch updated events
      queryClient.invalidateQueries({
        queryKey: ["public-booking", publicId],
      });
    },
  });
}
